// Piezas chicas de todas las pantallas: botón con el texto del color de la marca, vacíos, errores con reintento y
// avisos. Los esqueletos de carga, con la forma de cada pantalla, están en Esqueletos.tsx.
import { Boton as BotonUi, type BotonProps, cx, Icono } from '@reparto/ui';
import type { ReactNode } from 'react';

/** Al tocar o pasar el mouse sobre algo del color de la marca: un poco más oscuro, sin cambiar de tono (con una marca
 * clara, el 700 queda oscuro y el texto, también oscuro, dejaría de leerse). */
export const OSCURECER = 'hover:brightness-95 active:brightness-90';

/** El botón de @reparto/ui; el primario lleva el texto que se lee sobre el color de la marca. */
export function Boton({ className, ...p }: BotonProps) {
  const primario = (p.variante ?? 'primario') === 'primario';
  return (
    <BotonUi
      {...p}
      className={cx(
        primario &&
          'text-sobre-marca! hover:bg-primary-600! active:bg-primary-600! disabled:bg-stone-300! disabled:text-stone-700!',
        primario && OSCURECER,
        className,
      )}
    />
  );
}

export function Vacio({
  icono,
  titulo,
  texto,
  children,
}: {
  icono: string;
  titulo: string;
  texto?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center">
      <span className="grid h-20 w-20 place-items-center rounded-full bg-primary-50 text-primary-700">
        <Icono nombre={icono} tamano={40} />
      </span>
      <h2 className="mt-4 text-xl font-bold text-stone-900">{titulo}</h2>
      {texto ? <p className="mt-1 max-w-xs text-stone-600">{texto}</p> : null}
      {children ? <div className="mt-6 flex w-full max-w-xs flex-col gap-3">{children}</div> : null}
    </div>
  );
}

/** No se pudo traer algo: el motivo y "Probar de nuevo". */
export function ErrorCarga({
  mensaje,
  sinRed,
  onReintentar,
  compacto,
}: {
  mensaje: string | null;
  sinRed?: boolean;
  onReintentar: () => void;
  compacto?: boolean;
}) {
  if (compacto) {
    return (
      <div
        role="alert"
        className="flex items-center gap-3 rounded-2xl border border-stone-200 bg-white shadow-sm p-3 text-[15px]"
      >
        <Icono nombre={sinRed ? 'signal_disconnected' : 'error'} className="shrink-0 text-stone-500" />
        <span className="flex-1 text-stone-700">{mensaje}</span>
        <Boton variante="secundario" tamano="sm" className="h-11! shrink-0" onClick={onReintentar}>
          Reintentar
        </Boton>
      </div>
    );
  }
  return (
    <div role="alert">
      <Vacio
        icono={sinRed ? 'signal_disconnected' : 'cloud_off'}
        titulo={sinRed ? 'Sin señal' : 'No pudimos cargar esto'}
        texto={mensaje ?? undefined}
      >
        <Boton tamano="lg" icono="refresh" onClick={onReintentar}>
          Probar de nuevo
        </Boton>
      </Vacio>
    </div>
  );
}

type TonoAviso = 'info' | 'aviso' | 'peligro' | 'ok' | 'marca';
const AVISO: Record<TonoAviso, string> = {
  info: 'border-sky-200 bg-sky-50 text-sky-950',
  aviso: 'border-amber-200 bg-amber-50 text-amber-950',
  peligro: 'border-rose-200 bg-rose-50 text-rose-900',
  ok: 'border-emerald-200 bg-emerald-50 text-emerald-950',
  marca: 'border-primary-200 bg-primary-50 text-primary-950',
};
const ICONO_AVISO: Record<TonoAviso, string> = {
  info: 'info',
  aviso: 'campaign',
  peligro: 'error',
  ok: 'check_circle',
  marca: 'info',
};

export function Aviso({
  tono = 'info',
  icono,
  titulo,
  children,
  accion,
  className,
}: {
  tono?: TonoAviso;
  icono?: string;
  titulo?: string;
  children?: ReactNode;
  accion?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx('flex gap-3 rounded-2xl border p-3.5 text-[15px] leading-snug shadow-sm', AVISO[tono], className)}
    >
      <Icono nombre={icono ?? ICONO_AVISO[tono]} className="mt-px shrink-0" tamano={22} />
      <div className="min-w-0 flex-1">
        {titulo ? <p className="font-semibold">{titulo}</p> : null}
        {children ? <div className={cx(titulo && 'mt-0.5')}>{children}</div> : null}
        {accion ? <div className="mt-2.5 flex flex-wrap gap-2">{accion}</div> : null}
      </div>
    </div>
  );
}

/** Título de sección con una acción opcional a la derecha. */
export function TituloSeccion({ children, accion, id }: { children: ReactNode; accion?: ReactNode; id?: string }) {
  return (
    <div className="mb-3 flex min-h-11 items-center justify-between gap-3">
      <h2 id={id} className="text-xl font-bold text-stone-900">
        {children}
      </h2>
      {accion}
    </div>
  );
}

/**
 * Pantalla entera de espera, cuando todavía no se sabe qué mostrar: al abrir la tienda por primera vez (antes de saber
 * de qué empresa es), al entrar con el enlace o con Google. Con la marca y la cuenta, cada pantalla tiene su esqueleto.
 */
export function Cargando({ texto = 'Cargando…' }: { texto?: string }) {
  return (
    <div role="status" className="grid min-h-dvh place-items-center bg-fondo text-stone-600">
      <div className="flex items-center gap-2 text-lg">
        <Icono nombre="progress_activity" className="animate-spin text-primary-700" />
        {texto}
      </div>
    </div>
  );
}
